import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { TemplateService } from '../../car-wash-website/services/template.service';
import { LoggerService } from '../../services/logger.service';
import { BuilderContext } from './builder-context';

/** Patch emitted after a successful autosave so the component can update its references. */
export interface AutosaveStateUpdate {
  website?: any;
  currentJsonDataWebsite?: any;
}

/**
 * Handles debounced / throttled autosave for the website builder.
 * Owns the buffer, debounce timer, and throttle window.
 * Emits stateUpdate$ so the component can apply API response changes back to its fields.
 */
@Injectable({ providedIn: 'root' })
export class BuilderAutosaveService {

  /** Emits patches that the component should apply after a successful save. */
  readonly stateUpdate$ = new Subject<AutosaveStateUpdate>();

  private autosaveBuffer: Record<string, any> = {};
  private autosaveDebounce: any;
  private autosaveThrottleUntil = 0;
  private lastSaveAt = 0;

  constructor(
    private templateService: TemplateService,
    private logger: LoggerService
  ) {}

  /**
   * Queue one or more JSON paths to save and start the debounce countdown.
   * @param paths  JSON pointer strings (e.g. '/page/title', '/website/header').
   * @param currentJsonData  Full page JSON at the moment of the call.
   * @param ctx  Builder context with IDs needed for the API call.
   * @param websiteData  Website-level JSON (for website-level saves).
   * @param website  Raw website API response object.
   */
  schedule(
    paths: string[],
    currentJsonData: any,
    ctx: BuilderContext,
    websiteData?: any,
    website?: any
  ): void {
    for (const p of paths) {
      this.autosaveBuffer[p] = this.getJsonAtPath(currentJsonData, p);
    }

    const now = Date.now();
    const canSendNow = now >= this.autosaveThrottleUntil;

    clearTimeout(this.autosaveDebounce);
    this.autosaveDebounce = setTimeout(() => {
      if (canSendNow) {
        this.flush(currentJsonData, ctx, website);
        this.autosaveThrottleUntil = Date.now() + 2000;
      } else {
        const delay = Math.max(this.autosaveThrottleUntil - Date.now(), 0) + 10;
        clearTimeout(this.autosaveDebounce);
        this.autosaveDebounce = setTimeout(() => this.flush(currentJsonData, ctx, website), delay);
      }
    }, 800);

    if (now - this.lastSaveAt > 10000 && canSendNow) {
      clearTimeout(this.autosaveDebounce);
      this.autosaveDebounce = setTimeout(() => this.flush(currentJsonData, ctx, website), 500);
    }
  }

  /**
   * Immediately send queued paths to the API.
   * Called by schedule() after the debounce delay.
   */
  flush(currentJsonData: any, ctx: BuilderContext, website?: any): void {
    if (!ctx.websiteId) return;
    if (!currentJsonData) return;

    const hasWebsiteLevelChanges = Object.keys(this.autosaveBuffer)
      .some(path => path.startsWith('/website/'));

    if (hasWebsiteLevelChanges) {
      const formData = new FormData();
      const jsonData = website?.json_data || currentJsonData || {};

      if (!jsonData.website) jsonData.website = {};

      if (jsonData.website.header) {
        formData.append('header', JSON.stringify(jsonData.website.header));
      }
      if (jsonData.website.footer) {
        formData.append('footer', JSON.stringify(jsonData.website.footer));
      }
      formData.append('json_data', JSON.stringify(jsonData));

      this.templateService.updateWebsite(ctx.websiteId, formData).subscribe({
        next: (response) => {
          this.lastSaveAt = Date.now();
          Object.keys(this.autosaveBuffer).forEach(path => {
            if (path.startsWith('/website/')) {
              delete this.autosaveBuffer[path];
            }
          });
          this.logger.debug('Autosave: website-level data saved');

          if (response?.data) {
            const update: AutosaveStateUpdate = { website: response.data };
            if (response.data.json_data) {
              update.currentJsonDataWebsite = response.data.json_data.website;
            }
            this.stateUpdate$.next(update);
          }
        },
        error: (err) => this.logger.error('Autosave (website-level) failed:', err)
      });
    }

    const hasPageLevelChanges = !hasWebsiteLevelChanges ||
      Object.keys(this.autosaveBuffer).some(path => !path.startsWith('/website/'));

    if (hasPageLevelChanges) {
      if (!ctx.websitePageId) return;

      const cleanedJsonData = this.cleanupJsonStructure(currentJsonData);
      const htmlData = this.generateHtmlFromJson(cleanedJsonData);

      this.templateService.updateWebsitePage(
        ctx.websitePageId,
        cleanedJsonData,
        htmlData,
        ctx.websiteId
      ).subscribe({
        next: () => {
          this.lastSaveAt = Date.now();
          Object.keys(this.autosaveBuffer).forEach(path => {
            if (!path.startsWith('/website/')) {
              delete this.autosaveBuffer[path];
            }
          });
          this.logger.debug('Autosave: page-level data saved');
        },
        error: (err) => this.logger.error('Autosave (page-level) failed:', err)
      });
    }
  }

  /**
   * Remove legacy root-level keys from a JSON snapshot before saving.
   * Only removes them when the new nested structure (page.sections.*) exists.
   */
  cleanupJsonStructure(jsonData: any): any {
    if (!jsonData || typeof jsonData !== 'object') return jsonData;

    const cleaned = JSON.parse(JSON.stringify(jsonData));

    if (cleaned.page && cleaned.page.sections) {
      if (cleaned.page.sections.hero_section && cleaned.hero) {
        delete cleaned.hero;
      }
      if (cleaned.sections && Array.isArray(cleaned.sections)) {
        delete cleaned.sections;
      }
      if (cleaned.services) {
        delete cleaned.services;
      }
      if (cleaned.page.sections.testimonials_section && cleaned.testimonials) {
        delete cleaned.testimonials;
      }
      if (cleaned.page.sections.booking_section && cleaned.booking) {
        delete cleaned.booking;
      }
      if (cleaned.page.sections.contact_section && cleaned.contact) {
        delete cleaned.contact;
      }
    }

    return cleaned;
  }

  /**
   * Resolve a JSON-pointer-like path string into a value inside the JSON object.
   * Understands the special 'sections' array key for legacy structures.
   */
  getJsonAtPath(obj: any, pointer: string): any {
    if (!pointer || pointer === '/') return obj;
    const parts = pointer.replace(/^\//, '').split('/');
    let cur = obj;
    for (const key of parts) {
      if (cur == null) return undefined;
      if (key === 'sections' && Array.isArray(cur.sections)) {
        cur = cur.sections.find((s: any) => s.id === 'welcome' || s.type === 'welcome');
      } else {
        cur = cur[key];
      }
    }
    return cur;
  }

  /** Placeholder HTML generator — page data is persisted as JSON. */
  generateHtmlFromJson(_jsonData: any): string {
    return '<!DOCTYPE html><html><head><title>Website Page</title></head><body><h1>Page Content</h1></body></html>';
  }

  /** Clear the buffer and any pending timer (called on component destroy). */
  destroy(): void {
    clearTimeout(this.autosaveDebounce);
    this.autosaveBuffer = {};
  }
}
